import { CookieMonsterService } from "../cookie-monster/cookie-monster.service";

interface InsightsPopoverParams {
  analyticsHidePeriod: number;
  analyticsCookieName: string;
  insightsHideAccessInterval: number;
  insightsHideAccessCount: number;
  insightsCookieName: string;
}

export class InsightsPopoverService {
  /* @ngInject */
  constructor(
    private cookieMonsterService: CookieMonsterService,
    private INSIGHTS_POPOVER: InsightsPopoverParams
  ) {
  }

  /**
   * Determines if the Analytics popover should be displayed or not.
   *
   * - The Analytics tool-tip will be visible to all users;
   * - User can disable it;
   * - If user disables and hasn't logged into Naviance Insights in 2 weeks then show the Analytics tool-tip again.
   *
   * @param accessLevel string 'school' or 'district'
   * @return bool
   */
  public isAnalyticsPopoverDisplayed(accessLevel) {
    return this.getAnalyticsCookie(accessLevel).then((lastDisplayTime: number) => {
      lastDisplayTime = parseInt(lastDisplayTime + "", 10) || 0;

      if (!lastDisplayTime) {
        return true;
      }

      const diff = this.time - lastDisplayTime;

      if (diff > this.INSIGHTS_POPOVER.analyticsHidePeriod) {
        this.deleteAnalyticsCookie(accessLevel);
        this.deleteInsightsCookie(accessLevel);

        return true;
      }

      return false;
    });
  }

  /**
   * Determines if the Naviance Insights popover should be displayed or not.
   *
   * - Show tool-tip when the user hovers on Naviance Insights;
   * - The user has no ability to disable this tool-tip;
   * - Don't show this tool-tip any more if the user has accessed Naviance Insights 4 or more times
   *    within the last 30 days since disabling the Analytics tool-tip.
   *
   * @param accessLevel string 'school' or 'district'
   * @return bool
   */
  public isInsightsPopoverDisplayed(accessLevel) {
    return this.isAnalyticsPopoverDisplayed(accessLevel).then((isAnalyticsPopoverDisplayed: boolean) => {
      if (isAnalyticsPopoverDisplayed) {
        return true;
      }

      return this.getInsightsAccessCount(accessLevel).then((count: number) => {
        return count < this.INSIGHTS_POPOVER.insightsHideAccessCount;
      });
    });
  }

  public refreshAnalyticsPopoverDisplay(accessLevel) {
    return this.setCookie(this.INSIGHTS_POPOVER.analyticsCookieName, this.time, accessLevel);
  }

  private getInsightsAccessCount(accessLevel) {
    return this.getInsightsRequests(accessLevel).then(requests => {
      let count = 0;

      Object.keys(requests).forEach(date => {
        count += requests[date];
      });

      return count;
    });
  }

  private getInsightsRequests(accessLevel) {
    const dateFrom = this.today - this.INSIGHTS_POPOVER.insightsHideAccessInterval;

    return this.getInsightsCookie(accessLevel).then(requests => {
      requests = requests || {};
      Object.keys(requests).forEach(date => {
        if (Math.round(new Date(date).getTime() / 1000) < dateFrom) {
          delete requests[date];
        }
      });

      return requests;
    });
  }

  private getAnalyticsCookie(accessLevel) {
    return this.getCookie(this.INSIGHTS_POPOVER.analyticsCookieName, accessLevel);
  }

  private deleteAnalyticsCookie(accessLevel) {
    return this.deleteCookie(this.INSIGHTS_POPOVER.analyticsCookieName, accessLevel);
  }

  private getInsightsCookie(accessLevel) {
    return this.getCookie(this.INSIGHTS_POPOVER.insightsCookieName, accessLevel);
  }

  private deleteInsightsCookie(accessLevel) {
    return this.deleteCookie(this.INSIGHTS_POPOVER.insightsCookieName, accessLevel);
  }

  private getCookie(key, accessLevel) {
    return this.cookieMonsterService.get(this.getCookieKey(key, accessLevel));
  }

  private setCookie(key, value, accessLevel) {
    return this.cookieMonsterService.set(this.getCookieKey(key, accessLevel), value);
  }

  private deleteCookie(key, accessLevel) {
    return this.cookieMonsterService.delete(this.getCookieKey(key, accessLevel));
  }

  private getCookieKey(key, accessLevel) {
    return key + ":" + accessLevel;
  }

  private get time() {
    return Math.round((new Date()).getTime() / 1000);
  }

  private get today() {
    const date = new Date();

    date.setUTCHours(0, 0, 0, 0);

    return Math.round(date.getTime() / 1000);
  }
}



/** WEBPACK FOOTER **
 ** ./~/tslint-loader!./app/insights/insights-popover.service.ts
 **/